import moment from 'moment'
import { v4 as uuidv4 } from 'uuid'
import getPrincipal from '../../utils/getPrincipal'
import getCurrentUser from '../../utils/getCurrentUser'
import { WEIGHT_UNIT_LABEL } from '../../constants'
import { getStationByCode } from '../../services/searchPanelServices'
import { sendEmail } from '../../services/notificationService'
import sessionKeys from '../../constants/sessionKeys'
import { getFromSessionStorage } from '../../utils/StorageUtils'

// Enterprise/GSA users get agent-selection behavior distinct from regular consumer users.
export const isEnterpriseOrGsaUser = () => {
	const principal = getPrincipal()
	if (!principal) return false

	return principal.type === 'enterprise' || !!principal.roles?.includes('GSA_ADMIN_USER') || !!principal.roles?.includes('GSA_USER')
}

export const hasSlrUserDetails = (userDetails = getCurrentUser()) =>
	!!userDetails && typeof userDetails === 'object' && !Array.isArray(userDetails) && Object.keys(userDetails).length > 0

export const getSlrFallbackStation = () => {
	if (hasSlrUserDetails() || !isEnterpriseOrGsaUser()) return null
	try {
		const stored = JSON.parse(getFromSessionStorage(sessionKeys.SLR_FALLBACK_STATION))
		return stored?.userId === getPrincipal()?.sub && /^[A-Z]{3}$/.test(stored?.stationCode)
			? stored.stationCode
			: null
	} catch (error) {
		return null
	}
}

export const getDisplayStatus = (statusCode) => {
	switch (statusCode) {
		case 'N':
			return 'New'
		case 'C':
			return 'Booked'
		case 'P':
			return 'Under Review'
		case 'R':
			return 'Rejected'
		default:
			return 'New'
	}
}

// Translation key for UI status labels; getDisplayStatus stays English for the PDF.
export const getStatusLabelKey = (statusCode) => {
	switch (statusCode) {
		case 'C':
			return 'COMMON.BOOKED'
		case 'P':
			return 'BOOKING.SLR_STATUS_UNDER_REVIEW'
		case 'R':
			return 'VIEWBKG.ST_REJECTED'
		default:
			return 'BOOKING.SLR_STATUS_NEW'
	}
}

/**
 * Extracts email metrics from API response and component state
 * @param {Object} slrResponse - API response data from submitSlrBooking
 * @param {Array} packagingRows - Array of packaging row details
 * @param {Date|String} shippingDate - Shipping date to format
 * @param {Array|String} sccCodes - SCC codes (can be single array or combined from multiple)
 * @returns {Object} Extracted metrics object
 */
export const extractEmailMetricsFromResponse = (slrResponse, packagingRows, shippingDate, sccCodes) => {
	const totalPieces = packagingRows.reduce((sum, row) => sum + (Number(row.pieces) || 0), 0)
	const totalWeightStr = `${slrResponse.grossWeight} ${WEIGHT_UNIT_LABEL[slrResponse.grossWeightUnit] || slrResponse.grossWeightUnit}`
	const sccCodesStr = Array.isArray(sccCodes) ? sccCodes.join(', ') : sccCodes || ''
	const submittedDateTime = moment.utc(slrResponse.submittedAt).format('DD MMM YYYY HH:mm:ss [UTC]')
	const shippingDateFormatted = moment(shippingDate).format('DD MMM YYYY')

	return { totalPieces, totalWeightStr, sccCodesStr, submittedDateTime, shippingDateFormatted }
}

/**
 * Builds complete email request object with all attributes
 * @param {String} emailType - Type of email (slr_origin_station or slr_customer_acknowledgement)
 * @param {Array} toEmailAddresses - Email recipient addresses
 * @param {Object} slrResponse - API response data from submitSlrBooking
 * @param {Object} extractedMetrics - Object from extractEmailMetricsFromResponse()
 * @param {Object} userDetails - Current user details object
 * @param {Object} additionalFields - Component-specific fields (agentName, agentPhone, customerEmail, etc.)
 * @returns {Object} Email request object ready to send
 */
export const buildSlrEmailRequest = (
	emailType,
	toEmailAddresses,
	slrResponse,
	extractedMetrics,
	userDetails,
	additionalFields = {}
) => ({
	toEmailAddresses,
	subject: `SLR: ${slrResponse.slrReferenceNumber} | ${slrResponse.origin}-${slrResponse.destination} | ${slrResponse.commodity} | ${additionalFields.customerDetails || ''}`,
	templateName: 'slr_notification_email',
	attributes: {
		emailType,
		body:
			emailType === 'slr_origin_station'
				? 'A new SLR has been submitted for your review.'
				: 'Thank you for submitting your Special Load Request (SLR).',
		slrNo: slrResponse.slrReferenceNumber,
		orgDst: `${slrResponse.origin}-${slrResponse.destination}`,
		submittedDateTime: extractedMetrics.submittedDateTime,
		shippingDate: extractedMetrics.shippingDateFormatted,
		customerDetails: additionalFields.customerDetails || '',
		agentName: additionalFields.agentName || '',
		agentPhone: additionalFields.agentPhone || '',
		agentEmail: additionalFields.agentEmail || '',
		shippingItem: slrResponse.product,
		commodity: slrResponse.commodity,
		shipmentSCC: extractedMetrics.sccCodesStr,
		shipmentDescription: additionalFields.shippingDescription || '',
		totalPieces: `${extractedMetrics.totalPieces} pcs`,
		totalWeight: extractedMetrics.totalWeightStr,
		...(emailType === 'slr_customer_acknowledgement' && {
			disclaimerText:
				'This request is subject to review and approval based on availability and operational feasibility. You will be notified of the outcome shortly.',
		}),
	},
	hasAttachments: slrResponse.attachments?.length > 0,
	agentCode: additionalFields.agentCode || userDetails?.agentCode || '',
	userId: userDetails?.userId,
	async: false,
})

/**
 * Sends SLR notification emails (origin station + customer acknowledgement)
 * Handles complete workflow with parallel email sending and error handling
 * @param {Object} response - Complete API response from submitSlrBooking
 * @param {Array} packagingRows - Array of packaging row details
 * @param {Date|String} shippingDate - Shipping date
 * @param {Array|String} sccCodes - SCC codes
 * @param {Object} userDetails - Current user details
 * @param {Object} emailConfig - Component-specific configuration object with agentName, agentPhone, customerEmail, etc.
 */
export const sendSlrNotificationEmails = async (
	response,
	packagingRows,
	shippingDate,
	sccCodes,
	userDetails,
	emailConfig = {}
) => {
	try {
		const slrResponse = response.data
		const extractedMetrics = extractEmailMetricsFromResponse(slrResponse, packagingRows, shippingDate, sccCodes)

		const sendOriginStationEmail = async () => {
			try {
				const stationDetails = await getStationByCode(slrResponse.origin)
				if (stationDetails?.emailAddress) {
					const originEmails = stationDetails.emailAddress.split(',').map(email => email.trim())
					const originEmailReq = buildSlrEmailRequest(
						'slr_origin_station',
						originEmails,
						slrResponse,
						extractedMetrics,
						userDetails,
						emailConfig
					)
					await sendEmail(originEmailReq)
					console.log('[SlrUtils] Origin station email sent successfully')
				}
			} catch (err) {
				console.error('[SlrUtils] Failed to fetch station details or send origin email:', err)
			}
		}

		const sendCustomerEmail = async () => {
			const customerEmail = emailConfig.customerEmail || userDetails?.email
			if (customerEmail) {
				try {
					const customerEmailReq = buildSlrEmailRequest(
						'slr_customer_acknowledgement',
						[customerEmail],
						slrResponse,
						extractedMetrics,
						userDetails,
						emailConfig
					)
					await sendEmail(customerEmailReq)
					console.log('[SlrUtils] Customer acknowledgement email sent successfully')
				} catch (err) {
					console.error('[SlrUtils] Failed to send customer email:', err)
				}
			} else {
				console.log('[SlrUtils] Customer email not available, skipping customer email')
			}
		}
		await Promise.all([sendOriginStationEmail(), sendCustomerEmail()])
	} catch (emailErr) {
		console.error('[SlrUtils] Email notification error:', emailErr)
	}
}

/**
 * Builds the API request object for SLR list fetching
 * @param {Object} formData - Form data containing search criteria
 * @param {number} pageNumber - Current page number (1-indexed)
 * @param {Object} sortData - Sort configuration { sortBy, sortOrder }
 * @param {boolean} isGsaUser - Whether the user is GSA
 * @returns {Object} Request body for API call
 */
export const getSLRListRequest = (formData, pageNumber = 1, sortData = {}, isGsaUser) => {
	const { origin, destination, fromBookingDate, slrNumber, customerCode, shipmentDescription } = formData;
	const { sortBy, sortOrder } = sortData;
	let stationCodes = null;
	if (getPrincipal()?.type !== 'enterprise' && isGsaUser) {
		const userDetails = getCurrentUser();
		const stationCodesSet = new Set();
		if (userDetails?.stationCode) {
			stationCodesSet.add(userDetails.stationCode);
		}
		if (userDetails?.defaultAirportCode && userDetails.defaultAirportCode !== userDetails?.stationCode) {
			stationCodesSet.add(userDetails.defaultAirportCode);
		}
		if (Array.isArray(userDetails?.userAllowedStations)) {
			userDetails.userAllowedStations.forEach((station) => {
				if (station?.stationCode) {
					stationCodesSet.add(station.stationCode);
				}
			});
		}
		const fallbackStation = getSlrFallbackStation();
		stationCodes = fallbackStation ? [fallbackStation] : stationCodesSet.size > 0 ? Array.from(stationCodesSet) : null;
	}

	const request = {
		requestID: uuidv4(),
		origin: origin?.code || null,
		destination: destination?.code || null,
		shippingDate: fromBookingDate ? moment(fromBookingDate).format('DD-MMM-YYYY') : null,
		slrReferenceNumber: slrNumber && slrNumber.trim() ? slrNumber.trim() : null,
		pageNumber: pageNumber || 1,
		pageSize: 10,
		stationCodes,
		shipmentDescription: shipmentDescription && shipmentDescription.trim() ? shipmentDescription.trim() : null,
		sortBy: sortBy || null,
		sortOrder: sortOrder || null,
		agentCode: customerCode ? customerCode : null,
	};

	return request;
};


/**
 * Converts weight unit code to display value
 * @param {string} value - Weight unit code (K, L, T)
 * @returns {string} Display weight unit
 */
export const getWeightUnitValue = (value) => {
	if (value === 'K') return 'Kg';
	if (value === 'L') return 'Lb';
	if (value === 'T') return 'T';
	return '';
};
